import React, { useState } from "react";
import { addStandardCourseItem, deleteStandardCourseItem, saveStandardCourseDocumentFile, updateStandardCourseItem } from "../../../api/backend";
import { Modal, Notice } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { EmptyNote, PanelHeader, SessionSelect, errorText } from "./common";

const EMPTY = { title: "", description: "", url: "", required: false, featured: false, instructorViewOnly: false, sessionId: null };
const ROW = { display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderBottom: "1px solid var(--ga-line)", flexWrap: "wrap" };
const ICON = { width: 34, height: 34, borderRadius: 8, display: "grid", placeItems: "center", background: "var(--ga-soft)", color: "var(--ga-sub)", flexShrink: 0 };
const HINT = { fontSize: 12, color: "var(--ga-sub)" };

/**
 * Documents of a standard course: a link or an uploaded file. Once a version is published its documents never change;
 * a change goes into the next version.
 */
export default function DocumentsPanel({ draft, courseId, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const documents = draft.documents;

  const open = (doc) => {
    setError(null);
    setFile(null);
    setForm(doc ? { title: doc.title, description: doc.description || "", url: doc.url || "", required: doc.required, featured: doc.featured, instructorViewOnly: Boolean(doc.instructorViewOnly), sessionId: doc.sessionId } : EMPTY);
    setEditing(doc || {});
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { ...form, url: form.url.trim() || null };
      if (file) await saveStandardCourseDocumentFile(courseId, body, file, editing.id);
      else if (editing.id) await updateStandardCourseItem(courseId, "documents", editing.id, body);
      else await addStandardCourseItem(courseId, "documents", body);
      setEditing(null);
      onChanged();
    } catch (e) {
      setError(errorText(e, "The document could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (doc) => {
    setError(null);
    try {
      await deleteStandardCourseItem(courseId, "documents", doc.id);
      onChanged();
    } catch (e) {
      setError(errorText(e, "The document could not be removed."));
    }
  };

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <div className="panel" data-testid="documents-panel">
      <PanelHeader title="Documents" subtitle="Handouts and references students and instructors see in every class."
        action={<button type="button" className="b pri sm" onClick={() => open(null)}><I.plus s={12} />Add document</button>} />
      <div className="panel-b" style={{ padding: 0 }}>
        {error && !editing && (
          <div style={{ padding: "12px 18px 0" }}>
            <Notice kind="bad">
              {error}{" "}
              <button type="button" className="ib" aria-label="Close" style={{ width: 24, height: 24, verticalAlign: "middle" }} onClick={() => setError(null)}><I.x s={14} /></button>
            </Notice>
          </div>
        )}
        {documents.length === 0 ? <EmptyNote>No documents yet.</EmptyNote> : documents.map((d) => (
          <div key={d.id} data-testid={`document-${d.id}`} style={ROW}>
            <span style={ICON}><I.file s={16} /></span>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700 }}>{d.title}{d.required ? " (required)" : ""}</div>
              <div className="sub" style={{ fontSize: 12.5 }}>{d.hasFile ? `File: ${d.fileName}` : d.url || "Embedded content"}</div>
            </div>
            <button type="button" className="b sec sm" onClick={() => open(d)}><I.pencil s={12} />Edit</button>
            <button type="button" className="ib" aria-label={`Remove ${d.title}`} onClick={() => remove(d)}><I.x s={16} /></button>
          </div>
        ))}
      </div>

      <Modal
        open={Boolean(editing)}
        onClose={() => !saving && setEditing(null)}
        title={editing?.id ? "Edit document" : "Add document"}
        labelId="document-dialog-title"
        footer={(
          <>
            <button type="button" className="b ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
            <button type="button" className="b pri" onClick={save} disabled={saving || !form.title.trim()}>{saving ? "Saving…" : "Save document"}</button>
          </>
        )}
      >
        <label className="field">Title<span className="inp"><input value={form.title} onChange={set("title")} required /></span></label>
        <label className="field">Description<textarea className="ta" rows={2} value={form.description} onChange={set("description")} /></label>
        <div style={{ display: "grid", gap: 6 }}>
          <label className="field">Link (URL)<span className="inp"><input value={form.url} onChange={set("url")} /></span></label>
          <span style={HINT}>Or upload a file below.</span>
        </div>
        <div>
          <label className="b sec sm" style={{ cursor: "pointer" }}>
            {file ? file.name : editing?.hasFile ? `Replace file (${editing.fileName})` : "Upload a file"}
            <input type="file" hidden aria-label="Document file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <SessionSelect sessions={draft.sessions} value={form.sessionId} onChange={(sessionId) => setForm((f) => ({ ...f, sessionId }))} />
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={form.required} onChange={(e) => setForm((f) => ({ ...f, required: e.target.checked }))} />
            Required
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} />
            Featured
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" checked={form.instructorViewOnly} onChange={(e) => setForm((f) => ({ ...f, instructorViewOnly: e.target.checked }))} />
            Instructor view only
          </label>
        </div>
        {error && <Notice kind="bad">{error}</Notice>}
      </Modal>
    </div>
  );
}
